import { CancelledError, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { computeShouldForceExit } from "../../../../../hooks/useForceExitCheck";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useForceExitStore } from "../../../../../stores/forceExitStore";
import { applicationStatusQuery } from "../../../api/useApplicationStatus";
import { useSaveAllSections } from "../../../useSaveAllSections";

export interface CallScriptOpenOrchestrationOptions {
  open: boolean;
  approvalId: string | undefined;
  applicantId: string | undefined;
  readOnly: boolean;
  onOpenChange: (open: boolean) => void;
  /** Already-translated message shown when saveAll fails outright (no thrown error, just a non-ok response). */
  prepareSaveFailedMessage: string;
}

export interface CallScriptOpenOrchestrationResult {
  /** True while the pre-open save/force-exit check is running — callers show a loading skeleton. */
  isPreparingModal: boolean;
  /** Set when the pre-open save failed; callers show this with a Retry button instead of the form. */
  prepareError: string | undefined;
  forceExitWarningOpen: boolean;
  setForceExitWarningOpen: (open: boolean) => void;
  /** Re-runs the pre-open check without toggling `open` — wired to the error state's Retry button. */
  retry: () => void;
}

// ---------------------------------------------------------------------------
// useCallScriptOpenOrchestration
//
// Shared by both Call Script modals (CallScriptModal and
// CallScriptModalResidence): before either reveals its comparison form, CAPS2
// parity requires flushing any unsaved edits elsewhere on the Signers form
// first (so "Saved Information" is never built from a stale dashboard
// snapshot) and re-checking force-exit fresh, since the button-click-to-open
// guard's cached value could be stale by the time this effect runs.
//
// No JSX and no i18n beyond the one already-translated string callers pass
// in, so neither screen's translation namespace leaks in here.
// ---------------------------------------------------------------------------
/**
 * Runs the save-everything-first / force-exit-recheck orchestration shared by
 * both Call Script modals before they reveal their comparison form.
 * @param {CallScriptOpenOrchestrationOptions} options - The calling modal's open state and callbacks.
 * @returns {CallScriptOpenOrchestrationResult} The prep/error state and force-exit modal wiring for the caller to render.
 */
export function useCallScriptOpenOrchestration({
  open,
  approvalId,
  applicantId,
  readOnly,
  onOpenChange,
  prepareSaveFailedMessage,
}: CallScriptOpenOrchestrationOptions): CallScriptOpenOrchestrationResult {
  const queryClient = useQueryClient();
  const setShouldForceExit = useForceExitStore((state) => state.setShouldForceExit);
  const { saveAll } = useSaveAllSections();

  const [isPreparingModal, setIsPreparingModal] = useState(false);
  const [prepareError, setPrepareError] = useState<string>();
  const [forceExitWarningOpen, setForceExitWarningOpen] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    // Read-only deals have nothing to save (the visibility gate already keeps
    // the button from being reached in this state, but guard defensively) —
    // skip straight to showing the read-only comparison view.
    if (!open || !approvalId || !applicantId || readOnly) {
      return;
    }
    let cancelled = false;
    setIsPreparingModal(true);
    setPrepareError(undefined);
    void (async () => {
      // Tracks whether the force-exit branch below fired, so the `finally`
      // can leave `isPreparingModal` true rather than momentarily revealing
      // the comparison form while the parent processes `onOpenChange(false)`.
      let forceExiting = false;
      try {
        const status = await queryClient.fetchQuery({ ...applicationStatusQuery(approvalId), staleTime: 0 });
        const isForceExit = computeShouldForceExit({ isPreApprovalVoid: status.isPreApprovalVoid, readOnly });
        setShouldForceExit(isForceExit);
        if (isForceExit) {
          forceExiting = true;
          if (!cancelled) {
            onOpenChange(false);
            setForceExitWarningOpen(true);
          }
          return;
        }

        const result = await saveAll(approvalId);
        if (cancelled) {
          return;
        }
        if (!result.ok && result.apiCallMade) {
          setPrepareError(prepareSaveFailedMessage);
          return;
        }

        // saveAll already awaits the dashboard cache refresh internally
        // (see useSaveAllSections), so "Saved Information" is already built
        // from the just-saved data by the time this runs.
      } catch (error) {
        if (!cancelled && !(error instanceof CancelledError)) {
          setPrepareError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled && !forceExiting) {
          setIsPreparingModal(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- saveAll/queryClient/onOpenChange/prepareSaveFailedMessage are effectively stable per open+retry cycle; re-running on every render would refire the save
  }, [open, approvalId, applicantId, readOnly, retryToken]);

  return {
    isPreparingModal,
    prepareError,
    forceExitWarningOpen,
    setForceExitWarningOpen,
    retry: () => setRetryToken((token) => token + 1),
  };
}
